import React, { useCallback, useState } from 'react'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Input,
  Typography,
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { Button, useNotify, useRefresh, useTranslate } from 'react-admin'

import { Checkbox, Group, Stack } from '@mantine/core'
import { IconCornerDownRight } from '@tabler/icons-react'
import { LXDatePeriod } from '../../components/LXDatePeriod'
import { YoutubeRawImportType, YoutubeRawImportType_AllValues } from '../../helpers/Source.helper'
import { LXDropzone } from './../../components/LXDropzone/LXDropzone'
import { API } from './../../helpers/Api.helper'
import { DateHelper } from './../../helpers/Date.helper'
import { SourceHelper } from './../../helpers/Source.helper'
import { ImportStatus } from './ImportStatus'
import styles from './YTImportModal.module.scss'

// maps used both to validate and to auto-detect the import parameters from the dropped file name
const SOURCE_TO_FILE_TOKEN: Record<string, string> = {
  Logicomix: 'Logicomix',
  'Logicomix Network': 'Logicomix_Affiliate',
  Bump: 'Bump',
  Endemol: 'EndemolBeyondFR',
  'TV5 Monde': 'TV5Monde',
  'France TV Médias': 'France_T_l_visionsnull',
}

const TYPE_TO_FILE_SUFFIX: Record<YoutubeRawImportType, string> = {
  asset_summary: 'asset_summary',
  asset_summary_adj: 'ADJ_asset_summary',
  red_music_asset: 'red_music_rawdata_asset',
  red_non_music_asset: 'red_rawdata_asset',
  ecommerce_paid_feature: 'Ecommerce_paid_feature',
  shorts_non_music_subscriptions_video: 'monthly_shorts_non_music_subscription_video_summary',
  shorts_non_music_ads_video: 'monthly_shorts_non_music_ads_video_summary',
  channel_adjustment: 'channel_adjustments_summary',
  affiliate_tax_refund: 'affiliate_tax_refund_summary',
  affiliate_payment_summary: 'affiliate_payment_summary',
}

const FILE_TOKEN_TO_SOURCE: Record<string, string> = Object.fromEntries(
  Object.entries(SOURCE_TO_FILE_TOKEN).map(([sourceValue, token]) => [token, sourceValue]),
)

// best-effort detection of the import parameters from a file name like
// "YouTube_<source>_M_<YYYYMM(DD)>_<suffix>_v1-0.csv"
const detectFromFileName = (
  fileName: string,
): { source?: string; type?: YoutubeRawImportType; year?: number; month?: number } => {
  const detected: { source?: string; type?: YoutubeRawImportType; year?: number; month?: number } =
    {}

  const sourceToken = fileName.match(/^YouTube_([a-zA-Z0-9_]+)_M_/)?.[1]
  if (sourceToken && FILE_TOKEN_TO_SOURCE[sourceToken]) {
    detected.source = FILE_TOKEN_TO_SOURCE[sourceToken]
  }

  // match the most specific known suffix contained in the file name (longest first, so e.g.
  // ADJ_asset_summary wins over asset_summary); resilient to version / extension / "(1)" variations
  const detectedType = (Object.entries(TYPE_TO_FILE_SUFFIX) as [YoutubeRawImportType, string][])
    .sort(([, a], [, b]) => b.length - a.length)
    .find(([, suffix]) => fileName.includes(suffix))?.[0]
  if (detectedType) {
    detected.type = detectedType
  }

  const yearMonth = fileName.match(/_M_(\d{6,8})_/)?.[1]
  if (yearMonth) {
    const year = Number(yearMonth.slice(0, 4))
    const month = Number(yearMonth.slice(4, 6))
    if (month >= 1 && month <= 12) {
      detected.year = year
      detected.month = month
    }
  }

  return detected
}

export const YTImportModal = (props: YTListActionsProps): JSX.Element => {
  const { isModalOpen, setIsModalOpen } = props
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()

  const [importType, setImportType] = React.useState<YoutubeRawImportType>()
  const [files, setFiles] = useState([] as File[])
  const [source, setSource] = React.useState<string>('')
  const [month, setMonth] = useState<number | undefined>(DateHelper.currentDate().get('M'))
  const [year, setYear] = useState<number | undefined>(DateHelper.currentDate().get('y'))
  const [statementMonth, setStatementMonth] = useState<number | undefined>(
    DateHelper.currentDate().get('M'),
  )
  const [statementYear, setStatementYear] = useState<number | undefined>(
    DateHelper.currentDate().get('y'),
  )
  const [isDateUserSet, setIsDateUserSet] = useState(false)
  const [isStatementDate, setIsStatementDate] = useState(false)

  const [conversionRate, setConversionRate] = useState(1)
  const [isImporting, setIsImporting] = useState(false)
  const [importingId, setImportingId] = React.useState<string>()

  React.useEffect(() => {
    if (!isModalOpen) {
      setImportType(undefined)
      setFiles([])
      setSource('')
      setConversionRate(1)
      // setMonth(DateHelper.currentDate().get('M'))
      // setYear(DateHelper.currentDate().get('y'))
      setIsDateUserSet(false)
      setIsImporting(false)
      setImportingId(undefined)
    }
  }, [isModalOpen])

  // mark the date as user-chosen so a later file drop won't overwrite it
  const handleDateChange = useCallback(
    (month: string | undefined, year: string | undefined): void => {
      if (month) {
        setMonth(parseInt(month))
      }
      if (year) {
        setYear(parseInt(year))
      }

      setIsDateUserSet(true)
    },
    [],
  )
  const handleStatementDateChange = useCallback(
    (m: string | undefined, y: string | undefined): void => {
      if (m) {
        setStatementMonth(parseInt(m))
      }
      if (y) {
        setStatementYear(parseInt(y))
      }
    },
    [],
  )

  // prefill the import type, source and date from the dropped file name, but only the inputs
  // the user hasn't set yet
  const handleDropFiles = useCallback(
    (dropped: File[]) => {
      setFiles(dropped)
      const fileName = dropped[0]?.name
      if (!fileName) {
        return
      }
      const detected = detectFromFileName(fileName)
      if (!source && detected.source) {
        setSource(detected.source)
      }
      if (!importType && detected.type) {
        setImportType(detected.type)
      }
      if (!isDateUserSet && detected.year && detected.month) {
        setMonth(detected.month)
        setYear(detected.year)
      }
    },
    [source, importType, isDateUserSet],
  )

  const importReport = async () => {
    if (!importType || !month || !year) {
      return
    }
    const formData = new FormData()
    formData.append('type', importType)
    formData.append('source', source)
    formData.append('month', String(month))
    formData.append('year', String(year))

    if (isStatementDate) {
      formData.append('statementMonth', String(statementMonth))
      formData.append('statementYear', String(statementYear))
    }

    formData.append('conversionRateDollarToEuros', conversionRate.toString())
    formData.append('file', files[0])
    setIsImporting(true)

    try {
      // 1. New import initialization
      const importStep1 = await API.post<FormData>('/api/youtubeRawImports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      setImportingId(importStep1.data.id)
    } catch (e: any) {
      console.error(e.response)
      notify(
        `${translate('pages.youtubeRawImports.toast_importError')} - ${
          e.response?.data?.message || e.response?.data
        }`,
        { type: 'error' },
      )
      refresh()
      setIsImporting(false)
    }
  }

  const onCompleteImport = useCallback(
    ({ youtubeRawImportId, error }: { youtubeRawImportId: string; error?: any }) => {
      if (error) {
        notify(
          `${translate('pages.youtubeRawImports.toast_importError')} - ${
            error?.response?.data?.message ?? error?.response?.data
          }`,
          { type: 'error' },
        )
        refresh()
        setIsImporting(false)

        return
      }
      notify(translate('pages.youtubeRawImports.toast_importSuccess'), {
        type: 'success',
      })
      refresh()
      setIsModalOpen(false)
    },
    [notify, refresh, setIsModalOpen, translate],
  )

  const fileErrors = React.useMemo(() => {
    if (files.length === 0 || !source) {
      return []
    }
    const fileSource = SOURCE_TO_FILE_TOKEN[source] ?? source

    const fileSuffix = (importType && TYPE_TO_FILE_SUFFIX[importType]) ?? importType

    const errors: string[] = []
    const fileName = files[0].name

    if (fileSource) {
      const matchedSource = fileName.match(/^YouTube_([a-zA-Z0-9_]+)_M_/)
      if (!matchedSource || matchedSource.length < 1 || matchedSource[1] !== fileSource) {
        errors.push(
          ` - ${translate('pages.youtubeRawImports.fileChecks.badSource', {
            source: fileSource,
          })}`,
        )
      }
    }

    // channel-level affiliate reports use a YYYYMM date, other reports use YYYYMMDD
    const isYearMonthOnly =
      importType === 'channel_adjustment' ||
      importType === 'affiliate_tax_refund' ||
      importType === 'affiliate_payment_summary'
    const matchedDate = fileName.match(/_M_(\d{6,8})_/)
    const expectedYearMonth = `${year}${(month ?? 0).toString().padStart(2, '0')}`
    const expectedDate = isYearMonthOnly ? expectedYearMonth : `${expectedYearMonth}01`
    if (!matchedDate || matchedDate.length < 1 || matchedDate[1] !== expectedDate) {
      errors.push(
        ` - ${translate('pages.youtubeRawImports.fileChecks.badDate', {
          date: expectedDate,
        })}`,
      )
    }

    if (importType) {
      const matchedFileType = fileName.match(
        new RegExp(`\\d_${fileSuffix}_v\\d-\\d\\.(csv|csv\\.gz)$`),
      )
      if (!matchedFileType) {
        errors.push(
          ` - ${translate('pages.youtubeRawImports.fileChecks.badFiletype', {
            fileType: fileSuffix,
          })}`,
        )
      }
    }

    if (errors.length > 0) {
      errors.push(
        `<br/>${translate('pages.youtubeRawImports.fileChecks.expectedFileName', {
          expectedFileName: `YouTube_${fileSource}_M_${expectedDate}_${fileSuffix}_v1-1.csv(.gz)`,
        })}`,
      )
    }

    return errors
  }, [files, source, month, year, importType, translate])

  return (
    <Dialog aria-labelledby="youtube-import-modal" open={isModalOpen}>
      <DialogTitle id="youtube-import-modal">
        {translate('pages.youtubeRawImports.newReport')}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container direction="row" spacing={4}>
          <Grid item lg={6}>
            <SelectImportType
              label={translate('pages.youtubeRawImports.importType')}
              importType={importType}
              setImportType={setImportType}
            />
          </Grid>
          <Grid item lg={6}>
            <SelectSource
              label={translate('pages.youtubeRawImports.source')}
              source={source}
              setSource={setSource}
            />
          </Grid>
          <Grid item lg={6}>
            <LXDatePeriod
              month={month?.toString()}
              year={year?.toString()}
              onUpdate={handleDateChange}
            />
          </Grid>
          <Grid item lg={6}>
            <InputConversionRate
              label={translate('pages.youtubeRawImports.conversionRateDollarToEuros')}
              conversionRate={conversionRate}
              setConversionRate={setConversionRate}
            />
          </Grid>
        </Grid>
        <Grid container direction="row" spacing={4}>
          <Grid item lg={12}>
            <Stack mt="xl" gap="xs">
              <Checkbox
                checked={isStatementDate}
                label={translate('pages.youtubeRawImports.applyToOtherStatementPeriod')}
                onChange={event => setIsStatementDate(event.currentTarget.checked)}
              />
              {isStatementDate && (
                <Group>
                  <IconCornerDownRight />
                  <LXDatePeriod
                    month={statementMonth?.toString()}
                    year={statementYear?.toString()}
                    onUpdate={handleStatementDateChange}
                  />
                </Group>
              )}
            </Stack>
          </Grid>
        </Grid>
        <LXDropzone
          className={styles.dropzone}
          accept={{ '*': ['text/csv', '.gz'] }}
          onDrop={handleDropFiles}
        >
          {files.length > 0 ? (
            <p>{files[0].name}</p>
          ) : (
            <p>{translate('pages.youtubeRawImports.dropzonePlaceholder')}</p>
          )}
        </LXDropzone>
        {fileErrors.length > 0 && (
          <>
            <Typography variant="body1" color="textPrimary">
              {'⚠️ '}
              {translate('pages.youtubeRawImports.fileChecks.badFileName')}
            </Typography>
            <br />
            {fileErrors.map(error => (
              <Typography
                key={error}
                className={styles.error}
                variant="body1"
                color="textPrimary"
                dangerouslySetInnerHTML={{ __html: error }}
              />
            ))}
          </>
        )}
        {isImporting && importingId && (
          <ImportStatus
            youtubeRawImport={{ id: importingId, name: '' }}
            onComplete={onCompleteImport}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setIsModalOpen(false)
          }}
          label={translate('_generics.cancel')}
          disabled={isImporting}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            importReport()
          }}
          label={translate('_generics.import')}
          disabled={isImporting || !importType || source.length === 0 || files.length === 0}
        />
      </DialogActions>
    </Dialog>
  )
}

const SelectImportType: React.FC<SelectImportTypeProps> = ({
  label,
  importType,
  setImportType,
}) => {
  const translate = useTranslate()

  return (
    <FormControl fullWidth variant="standard">
      <InputLabel id="source-select-label">{label}</InputLabel>
      <Select
        labelId="source-select-label"
        id="demo-simple-select"
        required
        label={label}
        // controlled from the start ('' instead of undefined) so a programmatic
        // setImportType (e.g. file-drop auto-detect) is reflected by the Select
        value={importType ?? ''}
        onChange={e => setImportType(e.target.value as YoutubeRawImportType)}
      >
        {YoutubeRawImportType_AllValues.map(elt => (
          <MenuItem key={elt} value={elt}>
            {translate(`pages.youtubeRawImports.importTypes.${elt}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const SelectSource: React.FC<SelectSourceProps> = ({ label, source, setSource }) => (
  <FormControl fullWidth variant="standard">
    <InputLabel id="source-select-label">{label}</InputLabel>
    <Select
      labelId="source-select-label"
      id="demo-simple-select"
      required
      label={label}
      value={source}
      onChange={e => setSource(e.target.value)}
    >
      {SourceHelper.SOURCES.map(s => (
        <MenuItem key={s.value} value={s.value}>
          {s.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

const InputConversionRate = (props: InputConversionRateProps) => (
  <FormControl fullWidth>
    <InputLabel id="input-conversion-rate">{props.label}</InputLabel>
    <Input
      id="input-conversion-rate"
      type="number"
      value={props.conversionRate}
      inputProps={{ min: 0.01, step: 0.01 }}
      onChange={e => {
        props.setConversionRate(Number(e.target.value))
      }}
    />
  </FormControl>
)

type InputConversionRateProps = {
  label: string
  conversionRate: number
  setConversionRate: (cr: number) => void
}

type SelectImportTypeProps = {
  label: string
  importType?: YoutubeRawImportType
  setImportType: (s: YoutubeRawImportType) => void
}

type SelectSourceProps = {
  label: string
  source: string
  setSource: (s: string) => void
}

type YTListActionsProps = {
  isModalOpen: boolean
  setIsModalOpen: (b: boolean) => void
}
